// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import BunzlCategoryCard from "@store/Library/Components/Category/BunzlCategoryCard";
import Carousel, { CarouselSlide, CarouselStyles } from "@store/Overrides/Components/Carousel";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import uniqueId from "lodash/uniqueId";
import React, { useMemo } from "react";
import { css } from "styled-components";

type HasResponsive<T extends Partial<Record<string, any>> = {}> = { fields: T }[];
const maxNumberOfSlidesPerBreakpoint = [1, 2, 3, 4, 8];
interface CategoryResponsiveFields {
    numberOfItemsPerRow: number;
    carouselOverflow: boolean;
    fromCategory?: number;
    toCategory?: number;
    showArrows?: boolean;
    showDots?: boolean;
}
export type CategoryResponsiveFieldsBreakpoints = HasResponsive<CategoryResponsiveFields>;

interface BunzlStoreCategoryListBaseProps {
    fromCategory?: number;
    toCategory?: number;
    gap?: string;
    numberOfItemsPerRow?: number;
    showArrows?: boolean;
    carouselOverflow: boolean;
    showDots?: boolean;
    enableSkeleton?: boolean;
    responsiveFields?: CategoryResponsiveFieldsBreakpoints;
    shouldUseResponsiveFields?: boolean;
    isLoading?: boolean;
    categories?: Category[];
    linkText?: string;
}

export type BunzlStoreCategoryListProps = BunzlStoreCategoryListBaseProps;

export interface StoreCategoryListStyles {
    container?: InjectableCss;
    items?: InjectableCss;
    carousel?: CarouselStyles;
}

const storeCategoryListStyles: StoreCategoryListStyles = {
    carousel: {
        nextArrowWrapper: {
            css: css`
                display: none;
            `,
        },
        prevArrowWrapper: {
            css: css`
                display: none;
            `,
        },
        carouselSlidesContainer: {
            css: css`
                width: 100%;
                padding-bottom: ${({ theme }) => theme.design.layout.spacing.small};
                .is-selected {
                    padding: 0;
                }
            `,
        },
        carouselWrapper: {
            css: css`
                /* Carousel is overflowing on ios devices. We're targeting ios specifically with a fix as its not clear why this is happening and don't want to 
                change the existing styles that function on every other device */
                @media only screen and (min-device-width: 320px) and (max-device-width: 480px) and (-webkit-min-device-pixel-ratio: 2) {
                    max-width: 90vw;
                }
            `,
        },
    },
};

const getCarouselStyles = (
    showArrows: boolean,
    carouselStyles: CarouselStyles | undefined,
    gap: string,
): CarouselStyles | undefined => {
    if (!carouselStyles) {
        return carouselStyles;
    }
    if (!showArrows) {
        return {
            ...carouselStyles,
            carouselSlidesContainer: {
                css: css`
                    ${carouselStyles.carouselSlidesContainer?.css}
                `,
            },
            carouselSlideInner: {
                css: css`
                    ${carouselStyles.carouselSlideInner?.css}
                    padding: 0 calc(${gap} / 2);
                `,
            },
        };
    }
    return {
        ...carouselStyles,
        carouselSlidesContainer: {
            css: css`
                ${carouselStyles.carouselSlidesContainer?.css}
            `,
        },
        carouselSlideInner: {
            css: css`
                ${carouselStyles.carouselSlideInner?.css}
                padding: 0 calc(${gap} / 2);
            `,
        },
        nextArrowWrapper: {
            css: css`
                ${carouselStyles.nextArrowWrapper?.css}
                display: flex;
            `,
        },
        prevArrowWrapper: {
            css: css`
                ${carouselStyles.prevArrowWrapper?.css}
                display: flex;
            `,
        },
    };
};

export const getResponsiveCategoryFields = (
    breakpointIndex: number,
    baseFields: CategoryResponsiveFields,
    categoryResponsiveFields?: CategoryResponsiveFieldsBreakpoints,
    useResponsiveFields?: boolean,
): CategoryResponsiveFields => {
    if (!categoryResponsiveFields?.length || breakpointIndex === 0 || !useResponsiveFields) {
        return baseFields;
    }
    const currentBreakPointResponsiveFields: CategoryResponsiveFields | undefined =
        categoryResponsiveFields?.[breakpointIndex - 1]?.fields;

    const responsiveFields = {
        numberOfItemsPerRow: currentBreakPointResponsiveFields?.numberOfItemsPerRow ?? baseFields.numberOfItemsPerRow,
        carouselOverflow: currentBreakPointResponsiveFields?.carouselOverflow ?? baseFields.carouselOverflow,
        fromCategory: currentBreakPointResponsiveFields?.fromCategory ?? baseFields.fromCategory,
        toCategory: currentBreakPointResponsiveFields?.toCategory ?? baseFields.toCategory,
        showArrows: currentBreakPointResponsiveFields?.showArrows ?? baseFields.showArrows,
        showDots: currentBreakPointResponsiveFields?.showDots ?? baseFields.showDots,
    };

    return responsiveFields;
};
const BunzlStoreCategoryList: React.FC<BunzlStoreCategoryListProps> = ({
    categories,
    numberOfItemsPerRow = 1,
    gap = "0rem",
    isLoading,
    linkText,
    carouselOverflow,
    responsiveFields,
    toCategory,
    fromCategory,
    showArrows = false,
    showDots = true,
    enableSkeleton = true,
    shouldUseResponsiveFields: useResponsiveFields,
}) => {
    const breakpointIndex = useCurrentBreakpointIndex();
    const carouselId = useMemo(() => uniqueId(), []);

    const {
        numberOfItemsPerRow: responsiveNumberOfItemsPerRow,
        carouselOverflow: responsiveCarouselOverflow,
        toCategory: responsiveToCategory,
        fromCategory: responsiveFromCategory,
        showArrows: responsiveShowArrows,
        showDots: responsiveShowDots,
    } = useMemo(
        () =>
            getResponsiveCategoryFields(
                breakpointIndex ?? 0,
                {
                    numberOfItemsPerRow,
                    carouselOverflow,
                    fromCategory: fromCategory ?? 0,
                    toCategory: toCategory ?? 0,
                    showArrows: showArrows ?? false,
                    showDots: showDots ?? true,
                },
                responsiveFields,
                useResponsiveFields,
            ),
        [breakpointIndex, numberOfItemsPerRow, carouselOverflow, fromCategory, toCategory, responsiveFields],
    );

    const categoriesToDisplay = isLoading
        ? undefined
        : responsiveToCategory
          ? categories?.slice(responsiveFromCategory, responsiveToCategory)
          : categories;
    const slides: CarouselSlide[] =
        categoriesToDisplay?.map<CarouselSlide>(category => ({
            id: category.id,
            renderComponent: () => (
                <BunzlCategoryCard
                    key={category.id}
                    category={category}
                    linkText={linkText}
                    enableSkeleton={enableSkeleton}
                />
            ),
        })) ?? [];
    const maxNumberOfItemsPerRow = maxNumberOfSlidesPerBreakpoint[breakpointIndex ?? 0];
    const canBeCarousel = !isLoading && slides.length > Math.min(maxNumberOfItemsPerRow, responsiveNumberOfItemsPerRow);
    const showCarousel = canBeCarousel && responsiveCarouselOverflow;
    const columnCount = isLoading
        ? responsiveNumberOfItemsPerRow
        : Math.min(responsiveNumberOfItemsPerRow, slides.length);
    const containerStyles = {
        css: css`
            display: ${showCarousel ? "block" : "grid"};
            gap: ${gap};
            ${!showCarousel && `grid-template-columns: repeat(${columnCount}, 1fr);`}
        `,
    };
    const carouselStyles = getCarouselStyles(
        responsiveShowArrows ?? showArrows,
        storeCategoryListStyles?.carousel,
        gap,
    );

    return (
        <StyledWrapper {...containerStyles} data-test-selector="categoryList_container">
            {isLoading ? (
                Array.from({ length: responsiveNumberOfItemsPerRow }, (_, i) => <BunzlCategoryCard key={i} />)
            ) : !showCarousel ? (
                categoriesToDisplay?.map(category => (
                    <BunzlCategoryCard
                        key={category.id}
                        category={category}
                        linkText={linkText}
                        enableSkeleton={enableSkeleton}
                        data-test-selector={`categoryList_card_${category.id}`}
                    />
                ))
            ) : (
                <Carousel
                    showDots={responsiveShowDots}
                    slides={slides}
                    id={carouselId}
                    maxNumberOfColumns={responsiveNumberOfItemsPerRow}
                    extendedStyles={carouselStyles}
                    data-test-selector="categoryList_carousel"
                />
            )}
        </StyledWrapper>
    );
};
export const defaultCategoryCardCss = `.category-card {
}
.category-card-image {
}
.category-card-body {
}
.category-card-footer {
}
.category-card-product-title {
}
.category-card-link {
}
`;
export default BunzlStoreCategoryList;
